import last from 'lodash-es/last';
import defaultsDeep from 'lodash-es/defaultsDeep';
import delay from 'lodash-es/delay';
import merge from 'lodash-es/merge';
import endsWith from 'lodash-es/endsWith';
import each from 'lodash-es/each';
import updateStyle from '../css/updateStyle';
import updateStyleByRef from '../css/updateStyleByRef';
import lookAndFeel from '../lookAndFeel';
import {renderControls} from '../renderControls';
import { getDefaultControlStyle, getDefaultControl } from './toolbar.defaults';
import Control from './control';

export default class Toolbar extends Control {
  constructor(...args) {
    super(...args);
    const me = this;

    // initState — the immutable role of the toolbar (collapsed|expanded|static).
    // 'static' renders the toolbar always-expanded with no tab and disables
    // runtime state transitions. Reads `initState` from config, falls back to
    // legacy `state` field so components saved before the split keep working.
    this.addApi(this, 'initState',
      () => this._controlOptions.initState || this._controlOptions.state || 'collapsed');

    // state — the runtime open/closed state. Defaults to whatever initState
    // resolves to; setState() writes _state which survives render(). When
    // initState is 'static', state always reports 'static' and setState() is
    // a no-op.
    this.addApi(this, 'state',
      () => {
        const init = me.initState;
        return init === 'static' ? 'static' : init;
      });

    // setInitState — public API for the initial-state role.
    this.setInitState = function(value) {
      if (value !== 'collapsed' && value !== 'expanded' && value !== 'static') {
        throw new Error(
          "setInitState: expected 'collapsed' | 'expanded' | 'static', got '" + value + "'"
        );
      }

      const oldValue = me.initState;
      me._initState = value;
      // 'static' disables runtime state — drop any prior _state override so
      // the getter no longer reports stale runtime values.
      if (value === 'static') { me._state = undefined; }
      if (oldValue !== value) {
        me.raiseEvent('initStateChange', { value, oldValue });
      }

      me.render();
    };

    // Wrap auto-generated setState with synonyms, enum validation, and the
    // static-guard. 'static' is a silent no-op — use setInitState('static')
    // to change the initial role.
    const _setStateRaw = this.setState.bind(this);
    this.setState = function(state) {
      if (state === 'open') { state = 'expanded'; }
      else if (state === 'closed') { state = 'collapsed'; }

      // setState('static') is a no-op. The static role is set via
      // setInitState; runtime state covers collapsed/expanded only.
      if (state === 'static') { return; }

      if (state !== 'collapsed' && state !== 'expanded') {
        throw new Error(
          "setState: expected 'collapsed' | 'expanded' (or 'open' | 'closed'), got '" + state + "'"
        );
      }

      // Runtime open/close transitions only apply when initState allows
      // toggling. When the toolbar is configured 'static', setState is a
      // silent no-op.
      if (me.initState === 'static') { return; }

      const oldValue = me.state;
      _setStateRaw(state);
      if (oldValue !== state) {
        me.raiseEvent('isExpanded', { value: state, oldValue });
      }
    };
    this.setOpen = function() { me.setState('expanded'); };
    this.setClosed = function() { me.setState('collapsed'); };

    // Body-level opacity — applied to the inner panel (`bodyEl`) during
    // render so users can semi-transparent the slide-out without affecting
    // event handling. Schema source: options.style.normal.opacity.
    this.addApi(this, 'opacity',
      () => {
        const o = this._controlOptions.style
              && this._controlOptions.style.normal
              && this._controlOptions.style.normal.opacity;
        return o !== undefined ? o : 1;
      },
      { onChange: () => this.render() });
  }

  renderElement() {
    const control = this;
    let options = this._controlOptions;
    const instance = this.instance;
    const path = this.path;
    options = defaultsDeep(options, {
      position: 'top',
      style: {
        hideStraightBorderWhenCollapsed: false,
        normal: {
          zIndex: 500,
        },
      },
      tab: {
        align: 'middle',
        offset: 0,
      },
      dataLED: {
        position: 'opposite',
        visible: true,
        align: 'end',
        offset: 0,
        style: {
          icon:
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='${encodeURIComponent(ledColor)}' d='M12,2A7,7 0 0,1 19,9C19,11.38 17.81,13.47 16,14.74V17A1,1 0 0,1 15,18H9A1,1 0 0,1 8,17V14.74C6.19,13.47 5,11.38 5,9A7,7 0 0,1 12,2M9,21V20H15V21A1,1 0 0,1 14,22H10A1,1 0 0,1 9,21M12,4A5,5 0 0,0 7,9C7,11.05 8.23,12.81 10,13.58V16H14V13.58C15.77,12.81 17,11.05 17,9A5,5 0 0,0 12,4Z' /%3E%3C/svg%3E",
          active: {},
          normal: {},
        },
      },
    });

    options.dataLED.visible =
      options.dataLED.visible &&
      instance.datasources &&
      instance.datasources.autoreload &&
      instance.datasources.autoreload.mode == 'polling';

    if (!options.ioEnabled) {
      if (control.el && control.el.parentNode && control.el.parentNode.contains(control.el)) {
        control.el.parentNode.removeChild(control.el);
      }

      if (
        control.dataLEDEl &&
        control.dataLEDEl.parentNode &&
        control.dataLEDEl.parentNode.contains(control.dataLEDEl)
      ) {
        control.dataLEDEl.parentNode.removeChild(control.dataLEDEl);
      }

      return;
    }

    if (!control.toggle) {
      control.toggle = function(state) {
        // Tab-click toggle is disabled when initState is 'static' — the tab
        // is hidden in renderElement, so this guard is belt-and-braces.
        if (control.initState === 'static') { return; }
        if (state === undefined) {
          state = (control.state === 'expanded') ? 'collapsed' : 'expanded';
        }

        // Delegate to setState — validates, writes _state, fires isExpanded,
        // and triggers render. Single notification path.
        control.setState(state);
      };
    }

    if (!control.el) {
      control.el = getDefaultControl();
      control.el.addEventListener('mouseover', function() {
        control.isHover = true;
        if (control.isMouseDown) {
          control.isActive = true;
        }

        control.render();
      });
      control.el.addEventListener('mouseout', function() {
        control.isHover = false;
        if (control.isMouseDown) {
          control.isActive = false;
          control.isMouseDown = false;
        }

        control.render();
      });
      if (instance.args.designMode != 'io-layout' && instance.args.designMode != 'io-design') {
        last(control.el.getElementsByClassName('tab')).addEventListener('click', function() {
          control.toggle();
        });
      }

      instance.on('beforeloaddata', () => {
        if (!control.isLoading) {
          control.isLoading = Date.now();
          control.render();
        }
      });
      instance.on('loaddata', () => {
        if (control.isLoading) {
          if (Date.now() - control.isLoading > 300) {
            control.isLoading = false;
            control.render();
          } else {
            delay(function() {
              control.isLoading = false;
              control.render();
            }, 300 - Date.now() + control.isLoading);
          }
        }
      });
      // last(
      //   control.el.getElementsByClassName('data-loading-indicator')
      // ).addEventListener('pointerdown', function() {
      //   if (
      //     instance.args.designMode == 'io-layout' ||
      //     instance.args.designMode == 'io-design'
      //   ) {
      //     control.isLoading = Date.now();
      //     control.render();
      //     delay(function() {
      //       control.isLoading = false;
      //       control.render();
      //     }, 300);
      //   } else {
      //     instance.reload();
      //   }
      // });
      // if (instance.args.designMode !== 'io-layout') {
      //   var currrentState = control.state || options.state || 'collapsed';
      // }
    }

    var bodyEl = control.el.getElementsByClassName('body')[0];
    if (instance.args.designMode === 'io-layout') {
      bodyEl.classList.add('bggrid');
    }

    var tabEl = last(control.el.getElementsByClassName('tab'));
    // var toplineEl = tabEl.getElementsByClassName('topline')[0];
    var tongueEl = tabEl.getElementsByClassName('tongue')[0];
    var nbTongueEl = tabEl.getElementsByClassName('tongue-noborder')[0];
    var noborderEl = tabEl.getElementsByClassName('noborder')[0];
    var iconEl = tabEl.getElementsByClassName('icon')[0];
    var stylePatch = {};
    var bodyStylePatch = {};
    var tabStylePatch = {};
    // var toplineStylePatch = {};
    var tongueStylePatch = {};
    var nbTongueStylePatch = {};
    var noborderStylePatch = {};
    var iconStylePatch = {};
    var dataLEDStylePatch = {};

    var defaultStyle = getDefaultControlStyle(instance);
    merge(stylePatch, defaultStyle.normal);
    merge(bodyStylePatch, defaultStyle.body.normal);
    merge(tabStylePatch, defaultStyle.tab.normal);
    // merge(toplineStylePatch, defaultStyle.tab.topline.normal);
    merge(tongueStylePatch, defaultStyle.tab.tongue);
    merge(nbTongueStylePatch, defaultStyle.tab.tongue);
    merge(noborderStylePatch, defaultStyle.tab.noborder);
    merge(iconStylePatch, defaultStyle.tab.icon.normal);

    var dataLEDPosition = options.dataLED.position;
    if (dataLEDPosition == 'opposite') {
      switch (options.position) {
        case 'top':
          dataLEDPosition = 'bottom';
          break;
        case 'right':
          dataLEDPosition = 'left';
          break;
        case 'bottom':
          dataLEDPosition = 'top';
          break;
        case 'left':
          dataLEDPosition = 'right';
          break;
      }
    }

    var dataLEDEl = control.el.getElementsByClassName('data-loading-indicator')[0];
    if (dataLEDPosition == 'attached') {
      if (!dataLEDEl) {
        dataLEDEl = control.dataLEDEl;
        if (dataLEDEl) {
          control.el.appendChild(dataLEDEl);
          delete control.dataLEDEl;
        } else {
          throw new Error('data-loading-indicator not found');
        }
      }

      dataLEDPosition = options.position + 'Attached';
    } else {
      if (dataLEDEl) {
        control.parentElement.appendChild(dataLEDEl);
        control.dataLEDEl = dataLEDEl;
      } else {
        dataLEDEl = control.dataLEDEl;
      }
    }

    if (!dataLEDEl) {
      throw new Error('data-loading-indicator not found');
    }

    // control.parentElement.getElementsByClassName('data-loading-indicator')[0];
    if (options.dataLED.visible) {
      merge(dataLEDStylePatch, defaultStyle.dataLED.normal);
    } else {
      dataLEDStylePatch.display = 'none';
    }

    if (
      options.style.size === undefined ||
      (!endsWith(options.style.size, 'px') && !endsWith(options.style.size, '%'))
    ) {
      options.style.size = '64px';
    }

    if (options.position == 'right' || options.position == 'left') {
      options.style.normal.width = options.style.size;
    } else {
      //top|bottom
      options.style.normal.height = options.style.size;
    }

    if (options.style && options.style.normal) {
      if (options.style.normal.background) {
        options.style.normal.backgroundColor = options.style.normal.background;
        delete options.style.normal.background;
      }

      if (options.style.normal.shadowBlur !== undefined) {
        stylePatch.shadowBlur = options.style.normal.shadowBlur;
        // delete options.style.normal.shadowBlur;
      }

      if (options.style.normal.shadowColor !== undefined) {
        stylePatch.shadowColor = options.style.normal.shadowColor;
        // delete options.style.normal.shadowColor;
      }

      merge(bodyStylePatch, options.style.normal);
      if (options.style.normal !== undefined) {
        stylePatch.zIndex = options.style.normal.zIndex;
      }
    }

    if (options.dataLED.visible) {
      merge(dataLEDStylePatch, options.dataLED.style.normal);
    }

    if (control.isHover) {
      merge(stylePatch, defaultStyle.hover);
      merge(bodyStylePatch, defaultStyle.body.hover);
      merge(iconStylePatch, defaultStyle.tab.icon.hover);
      // if (instance.args.designMode == 'io-layout') {
      //   merge(dataLEDStylePatch, defaultStyle.dataLED.active);
      // }

      if (options.style && options.style.hover) {
        if (options.style.hover.background) {
          options.style.hover.backgroundColor = options.style.hover.background;
          delete options.style.hover.background;
        }

        if (options.style.hover.shadowBlur !== undefined) {
          stylePatch.shadowBlur = options.style.hover.shadowBlur;
          // delete options.style.hover.shadowBlur;
        }

        if (options.style.hover.shadowColor !== undefined) {
          stylePatch.shadowColor = options.style.hover.shadowColor;
          // delete options.style.hover.shadowColor;
        }

        merge(bodyStylePatch, options.style.hover);

        // if (
        //   instance.args.designMode == 'io-layout' &&
        //   options.dataLED &&
        //   options.dataLED.style
        // ) {
        //   merge(dataLEDStylePatch, options.dataLED.style.active);
        // }
      }
    }

    if (options.dataLED.visible && control.isLoading) {
      merge(dataLEDStylePatch, defaultStyle.dataLED.active);
      if (options.dataLED && options.dataLED.style) {
        merge(dataLEDStylePatch, options.dataLED.style.active);
      }
    }

    merge(stylePatch, defaultStyle[options.position].normal);
    merge(bodyStylePatch, defaultStyle.body[options.position].normal);
    merge(tabStylePatch, defaultStyle.tab[options.position].middle);
    merge(tabStylePatch, defaultStyle.tab[options.position][options.tab.align]);
    merge(dataLEDStylePatch, defaultStyle.dataLED[dataLEDPosition][options.dataLED.align]);

    if (options.tab.offset) {
      tabStylePatch.offset = options.tab.offset;
    }

    // console.log({ tabOffset: tabStylePatch.offset });
    if (tabStylePatch.offset >= 0) {
      tabStylePatch.offset =
        (options.tab && options.tab.align == 'end' ? ' - ' : ' + ') + tabStylePatch.offset + 'px';
    } else {
      tabStylePatch.offset =
        (options.tab && options.tab.align == 'end' ? ' + ' : ' - ') +
        +Math.abs(tabStylePatch.offset) +
        'px';
    }

    dataLEDStylePatch.offset = options.dataLED.offset;

    if (dataLEDStylePatch.offset >= 0) {
      dataLEDStylePatch.offset =
        (options.dataLED.align == 'end' ? ' - ' : ' + ') + dataLEDStylePatch.offset + 'px';
    } else {
      dataLEDStylePatch.offset =
        (options.dataLED.align == 'end' ? ' + ' : ' - ') +
        +Math.abs(dataLEDStylePatch.offset) +
        'px';
    }

    // Effective render-time state: the toolbar's current `state` (which
    // resolves to initState when no runtime override is set), with a design-
    // mode override applied for the editor's io-layout / io-design preview
    // panes. We compute into a LOCAL — never write back to control.state —
    // so the editor preview doesn't permanently overwrite the user's runtime
    // setState() value via the setter that lands in _state.
    var effectiveState = control.state;
    if (
      instance.args.designMode == 'io-layout' ||
      (instance.args.designMode == 'io-design' && effectiveState == 'collapsed')
    ) {
      effectiveState = 'expanded';
    }

    if (
      (!effectiveState || effectiveState == 'collapsed') &&
      instance.args.designMode != 'io-layout' &&
      instance.args.designMode != 'io-design'
    ) {
      merge(stylePatch, defaultStyle.collapsed);
      merge(stylePatch, defaultStyle[options.position].collapsed);

      merge(bodyStylePatch, defaultStyle.body.collapsed);
      merge(bodyStylePatch, defaultStyle.body[options.position].collapsed);

      merge(tabStylePatch, defaultStyle.tab.collapsed);
    } else {
      if (effectiveState == 'static') {
        tabStylePatch.display = 'none';
      } else if (
        effectiveState == 'expanded' ||
        instance.args.designMode == 'io-layout' ||
        instance.args.designMode == 'io-design'
      ) {
        // merge(toplineStylePatch, defaultStyle.tab.topline.expanded);
        merge(iconStylePatch, defaultStyle.tab.icon.expanded);
      }
    }
    // console.log(stylePatch);
    // console.log(tabStylePatch);
    // console.log(tongueStylePatch);
    // console.log(iconStylePatch);

    const lnf = lookAndFeel(options.lookAndFeel, stylePatch.height);
    var templateVars = merge(
      {
        borderWidth: bodyStylePatch.borderWidth,
        color: bodyStylePatch.color,
        borderColor: bodyStylePatch.borderColor,
        backgroundColor: bodyStylePatch.backgroundColor,
        borderRadius: bodyStylePatch.borderRadius,
        tabOffset: tabStylePatch.offset,
        shadowBlur: stylePatch.shadowBlur,
        shadowColor: stylePatch.shadowColor,
        ledColor: options.dataLED.style.color || lnf.schemeForeground,
        ledOffset: dataLEDStylePatch.offset,
        // opacity: bodyStylePatch.opacity
        collapsedOffset:
          options.style.hideStraightBorderWhenCollapsed && effectiveState == 'collapsed'
            ? '0px'
            : '3px',
      },
      lnf
    );
    templateVars.iconUrl = options.dataLED.style.icon
      .replace('%23FFFFFF', templateVars.ledColor.replace('#', '%23'))
      .replace('%23000000', templateVars.ledColor.replace('#', '%23'));
    // console.log(templateVars);
    if (options.style.hideStraightBorderWhenCollapsed && effectiveState == 'collapsed') {
      tongueStylePatch.stroke = 'transparent';
      tongueStylePatch.fill = 'transparent';
    } else {
      nbTongueStylePatch.stroke = 'transparent';
      nbTongueStylePatch.fill = 'transparent';
    }

    updateStyleByRef(
      [
        stylePatch,
        bodyStylePatch,
        tabStylePatch,
        // toplineStylePatch,
        tongueStylePatch,
        nbTongueStylePatch,
        noborderStylePatch,
        iconStylePatch,
        dataLEDStylePatch,
      ],
      templateVars
    );

    //console.log(tabStylePatch);

    // if (bodyStylePatch.opacity !== undefined) {
    //   stylePatch.opacity = bodyStylePatch.opacity;
    //   delete bodyStylePatch.opacity;
    // }

    updateStyle(control.el, stylePatch, control.currentStyle);
    control.currentStyle = stylePatch;

    // Apply runtime opacity (setOpacity / opacity = …) to the body element
    // — the slide-out panel — without touching the outer container which
    // owns positioning/clipping.
    if (control.opacity !== undefined && control.opacity !== null) {
      bodyStylePatch.opacity = control.opacity;
    }

    // Mirror the base's blur/greyscale CSS classes onto bodyEl. The base
    // applyGenericOptions sets them on control.el AFTER renderElement
    // returns, so reading control.el.classList here would lag one render.
    // Read the source-of-truth flags directly instead. CSS filter inherits
    // through the DOM, but some intermediate template wrappers can break
    // the visual chain — applying directly to bodyEl is the reliable form.
    bodyEl.classList.remove('blur-fast');
    bodyEl.classList.remove('bw-fast');
    bodyEl.classList.remove('dim-fast');
    if (control.blur && control.greyscale) {
      bodyEl.classList.add('dim-fast');
    } else if (control.blur) {
      bodyEl.classList.add('blur-fast');
    } else if (control.greyscale) {
      bodyEl.classList.add('bw-fast');
    }

    updateStyle(bodyEl, bodyStylePatch, control.currentBodyStyle);
    control.currentBodyStyle = bodyStylePatch;

    updateStyle(tabEl, tabStylePatch, control.currentTabStyle);
    control.currentTabStyle = tabStylePatch;

    // updateStyle(toplineEl, toplineStylePatch, control.currentToplineStyle);
    // control.currentToplineStyle = toplineStylePatch;

    updateStyle(tongueEl, tongueStylePatch, control.currentTongueStyle);
    control.currentTongueStyle = tongueStylePatch;

    updateStyle(nbTongueEl, nbTongueStylePatch, control.currentNbTongueStyle);
    control.currentNbTongueStyle = nbTongueStylePatch;

    updateStyle(noborderEl, noborderStylePatch, control.currentNoborderStyle);
    control.currentNoborderStyle = noborderStylePatch;

    updateStyle(iconEl, iconStylePatch, control.currentIconStyle);
    control.currentIconStyle = iconStylePatch;

    updateStyle(dataLEDEl, dataLEDStylePatch, control.currentdataLEDStylePatch);
    control.currentdataLEDStylePatch = dataLEDStylePatch;

    if (options.controls) {
      each(options.controls, function(ctrl) {
        ctrl.lookAndFeel = merge({}, options.lookAndFeel, ctrl.lookAndFeel);
        // console.log('ctrl.lookAndFeel', ctrl.lookAndFeel);
      });
      renderControls(instance, path,control.nicePath, bodyEl);
    }

    return control.el;
  }
}
